import {
  FollowButtonDisplay,
  FollowButtonOutline,
  FollowButtonSize,
  FollowButtonVariant,
  FollowType,
  useTrackImpressionEventByIntersection,
} from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { useUserFollow } from "~/components/follow-button/hooks";
import { usePlusDetailedListingPopup } from "~/components/plus/detailed-listing-popup/hooks";
import type { TypeMagazinesStyleVariant } from "~/components/section/section-style/const";
import { MagazinesStyleVariant } from "~/components/section/section-style/const";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { Event } from "~/components/tracking/google-analytics/types";
import type { articleTopicFollowWidgetArticle$key } from "~/queries/__generated__/articleTopicFollowWidgetArticle.graphql";

import type { StyledProps } from "./styles";
import {
  Container,
  Divider,
  PlusTopicLink,
  StyledEntityFollowButton,
  StyledMyNewsPromotionWidget,
  StyledTopicLink,
} from "./styles";

type Props = {
  className?: string;
  reference: articleTopicFollowWidgetArticle$key;
  variant?: "banner" | "stack" | "plus" | TypeMagazinesStyleVariant;
} & Partial<PropsFromStyledProps<StyledProps>>;

export const ArticleTopicFollowWidget: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  variant = "banner",
}) => {
  const { handleOpen } = usePlusDetailedListingPopup();
  const { topics } = useFragment(
    graphql`
      fragment articleTopicFollowWidgetArticle on Article {
        topics {
          ...topicLinkTopic
          ...entityFollowButtonBase
          disableFollow
          entityId
          name
          entityUuid
        }
      }
    `,
    reference_,
  );

  const impressionEvent = useCallback<() => Event>(
    () => ({
      action: "Notification Center/Follow/Impression",
      category: "Notification Center",
      label: window.location.href,
    }),
    [],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    Event,
    null
  >({
    gaTrackingHandler: sendGATracking,
    getEvent: variant === "banner" ? impressionEvent : null,
    options: { isSendGATracking: variant === "banner", shouldSendOnce: true },
  });

  const { checkIsFollowed } = useUserFollow();

  if (!topics) return null;
  const mainTopic = first(topics);
  if (!mainTopic || mainTopic.disableFollow) return null;
  const isFollowed = checkIsFollowed(FollowType.Topic, mainTopic.entityId);

  const isBanner = variant === "banner";
  const isMagazinesStyle = variant === MagazinesStyleVariant;
  const isPlus = variant === "plus";
  const isStack = variant === "stack";

  const renderTopicLink = () => {
    switch (variant) {
      case "plus":
        return (
          <PlusTopicLink
            onClick={() => {
              handleOpen({ entityUuid: mainTopic.entityUuid, type: "topic" });
            }}
          >
            {mainTopic.name}
          </PlusTopicLink>
        );

      default:
        return <StyledTopicLink reference={mainTopic} />;
    }
  };

  return (
    <Container
      $isFollowed={isFollowed}
      $variant={variant}
      className={className}
      ref={captureTrackImpressionEventTargetElement}
    >
      {renderTopicLink()}
      <Divider />
      <StyledEntityFollowButton
        display={isPlus ? FollowButtonDisplay.BluePlusIcon : FollowButtonDisplay.Text}
        isDisabledTooltip={isMagazinesStyle}
        isLoadingEnabled={isPlus}
        outline={isBanner ? FollowButtonOutline.Plain : FollowButtonOutline.Outlined}
        reference={mainTopic}
        size={isBanner ? FollowButtonSize.Compact : FollowButtonSize.Small}
        source="Article_top"
        variant={FollowButtonVariant.Light}
        isTooltipFlipDisabled
      />
      {isStack && <StyledMyNewsPromotionWidget />}
    </Container>
  );
};

ArticleTopicFollowWidget.displayName = "ArticleTopicFollowWidget";
